import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import type { UserTypeDto, UserTypeMfa } from "@platform/core";
import {
  Alert,
  Badge,
  Button,
  DataGrid,
  Dialog,
  EmptyState,
  Field,
  Input,
  Label,
  MultiSelect,
  NativeSelect,
  Switch,
  Textarea,
  useToast,
  type GridColDef,
} from "@platform/ui";
import { isApiError } from "../../api/errors";
import { useApi, useConsole } from "../../context";
import { useT, type MessageKey } from "../../i18n";
import { SLUG_PATTERN, SLUG_RE, groupOptions, linesToList, useGroups, useUserTypes } from "../../lib/access";
import { errorText } from "../../lib/errors";
import { useGridText } from "../../lib/grid";
import { ConfirmDialog } from "../../layout/ConfirmDialog";
import { ErrorBanner, PageCard, ReloadButton } from "../../layout/Page";

const MFA_RULES: UserTypeMfa[] = ["inherit", "optional", "required"];
const MFA_LABEL: Record<UserTypeMfa, MessageKey> = {
  inherit: "userTypes.mfa.inherit",
  optional: "userTypes.mfa.optional",
  required: "userTypes.mfa.required",
};

function UserTypeDialog({ type, onClose }: { type: UserTypeDto | "new"; onClose: () => void }) {
  const { t } = useT();
  const api = useApi();
  const toast = useToast();
  const qc = useQueryClient();
  const groups = useGroups();
  const isNew = type === "new";
  const [id, setId] = useState(isNew ? "" : type.id);
  const [name, setName] = useState(isNew ? "" : type.name);
  const [description, setDescription] = useState(isNew ? "" : (type.description ?? ""));
  const [selfSignUp, setSelfSignUp] = useState(isNew ? false : type.policy.selfSignUp);
  const [domains, setDomains] = useState(isNew ? "" : type.policy.emailDomains.join("\n"));
  const [mfa, setMfa] = useState<UserTypeMfa>(isNew ? "inherit" : type.policy.mfa);
  const [defaultGroups, setDefaultGroups] = useState<string[]>(isNew ? [] : type.policy.defaultGroups);
  const policy = { selfSignUp, emailDomains: linesToList(domains), mfa, defaultGroups };
  const save = useMutation({
    mutationFn: () =>
      isNew
        ? api.userTypes.create({
            id,
            name: name.trim() || id,
            ...(description.trim() ? { description: description.trim() } : {}),
            policy,
          })
        : api.userTypes.update(type.id, {
            name: name.trim(),
            description: description.trim() || null,
            policy,
          }),
    onSuccess: () => {
      toast({ title: t("common.saved"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["user-types"] });
      onClose();
    },
  });
  const fe = isApiError(save.error) ? save.error.fieldErrors() : {};
  const domainError = Object.entries(fe).find(([k]) => k.includes("emailDomains"))?.[1];
  const valid = isNew ? SLUG_RE.test(id) : name.trim().length > 0;
  const switchId = "user-type-self-sign-up";
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      size="lg"
      title={isNew ? t("userTypes.createTitle") : t("userTypes.editTitle", { name: type.name })}
      closeLabel={t("common.close")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            data-testid="save-user-type"
            loading={save.isPending}
            disabled={!valid}
            onClick={() => save.mutate()}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      {save.isError && (
        <Alert tone="error" className="mb-4">
          {errorText(save.error)}
        </Alert>
      )}
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Field
          label={t("userTypes.id")}
          hint={t("userTypes.idHint")}
          required
          error={fe.id ?? (isNew && id && !SLUG_RE.test(id) ? t("access.slugInvalid") : undefined)}
        >
          {(p) => (
            <Input
              {...p}
              value={id}
              disabled={!isNew}
              pattern={SLUG_PATTERN}
              placeholder="contractor"
              autoComplete="off"
              onChange={(e) => setId(e.target.value.toLowerCase())}
            />
          )}
        </Field>
        <Field label={t("userTypes.name")} required={!isNew} error={fe.name}>
          {(p) => (
            <Input
              {...p}
              value={name}
              maxLength={100}
              placeholder={id}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
      </div>
      <Field label={t("userTypes.description")}>
        {(p) => (
          <Input
            {...p}
            value={description}
            maxLength={500}
            onChange={(e) => setDescription(e.target.value)}
          />
        )}
      </Field>
      <div className="mb-4 flex items-start gap-3">
        <Switch
          id={switchId}
          checked={selfSignUp}
          onCheckedChange={setSelfSignUp}
          label={t("userTypes.selfSignUp")}
        />
        <div>
          <Label htmlFor={switchId} className="mb-0">
            {t("userTypes.selfSignUp")}
          </Label>
          <p className="text-xs text-ink-2">{t("userTypes.selfSignUpHint")}</p>
        </div>
      </div>
      <Field label={t("userTypes.emailDomains")} hint={t("userTypes.emailDomainsHint")} error={domainError}>
        {(p) => (
          <Textarea
            {...p}
            data-testid="user-type-domains"
            placeholder={"example.com\n*.example.com"}
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
          />
        )}
      </Field>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Field label={t("userTypes.mfa")} hint={t("userTypes.mfaHint")}>
          {(p) => (
            <NativeSelect {...p} value={mfa} onChange={(e) => setMfa(e.target.value as UserTypeMfa)}>
              {MFA_RULES.map((m) => (
                <option key={m} value={m}>
                  {t(MFA_LABEL[m])}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t("userTypes.defaultGroups")} hint={t("userTypes.defaultGroupsHint")}>
          {(p) => (
            <MultiSelect
              {...p}
              label={t("userTypes.defaultGroups")}
              value={defaultGroups}
              onChange={setDefaultGroups}
              options={groupOptions(groups.data?.items)}
              placeholder={t("common.none")}
              emptyText={t("groups.empty")}
              searchPlaceholder={t("common.search")}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

export function UserTypesPage() {
  const { t } = useT();
  const api = useApi();
  const { can } = useConsole();
  const qc = useQueryClient();
  const toast = useToast();
  const types = useUserTypes();
  const [editing, setEditing] = useState<UserTypeDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<UserTypeDto | null>(null);
  const del = useMutation({
    mutationFn: (id: string) => api.userTypes.remove(id),
    onSuccess: () => {
      setDeleting(null);
      toast({ title: t("common.saved"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["user-types"] });
    },
  });
  const editable = can("settings:write");
  const text = useGridText();
  const columns = useMemo<GridColDef<UserTypeDto>[]>(
    () => [
      {
        field: "name",
        headerName: t("userTypes.name"),
        minWidth: 220,
        hideable: false,
        valueFormatter: (_v, r) => `${r.name} (${r.id})`,
        renderCell: ({ row: r }) => (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium">{r.name}</span>
              {r.builtIn && <Badge>{t("roles.builtIn")}</Badge>}
            </div>
            <p className="truncate font-mono text-xs text-ink-2">{r.id}</p>
          </div>
        ),
      },
      { field: "userCount", headerName: t("userTypes.accounts"), type: "number", width: 110 },
      {
        field: "selfSignUp",
        headerName: t("userTypes.selfSignUp"),
        type: "boolean",
        width: 130,
        valueGetter: (r) => r.policy.selfSignUp,
        valueFormatter: (v) => (v ? t("common.yes") : t("common.no")),
        renderCell: ({ row: r }) => (
          <Badge dot tone={r.policy.selfSignUp ? "success" : "neutral"}>
            {r.policy.selfSignUp ? t("common.yes") : t("common.no")}
          </Badge>
        ),
      },
      {
        field: "emailDomains",
        headerName: t("userTypes.emailDomains"),
        minWidth: 180,
        sortable: false,
        valueGetter: (r) => r.policy.emailDomains.join(", "),
        renderCell: ({ row: r }) =>
          r.policy.emailDomains.length ? (
            <span className="font-mono text-xs">{r.policy.emailDomains.join(", ")}</span>
          ) : (
            <span className="text-ink-3">{t("common.none")}</span>
          ),
      },
      {
        field: "mfa",
        headerName: t("userTypes.mfa"),
        width: 160,
        valueGetter: (r) => t(MFA_LABEL[r.policy.mfa]),
      },
      {
        field: "defaultGroups",
        headerName: t("userTypes.defaultGroups"),
        minWidth: 180,
        sortable: false,
        valueGetter: (r) => r.policy.defaultGroups.join(", "),
        renderCell: ({ row: r }) => (
          <div className="flex flex-wrap gap-1">
            {r.policy.defaultGroups.length === 0 ? (
              <span className="text-ink-3">{t("common.none")}</span>
            ) : (
              r.policy.defaultGroups.map((g) => (
                <Badge key={g} tone="info">
                  {g}
                </Badge>
              ))
            )}
          </div>
        ),
      },
      {
        field: "actions",
        headerName: "",
        type: "actions",
        width: 170,
        hideable: false,
        resizable: false,
        disableExport: true,
        align: "right",
        renderCell: ({ row: r }) =>
          editable ? (
            <>
              <Button
                variant="text"
                size="sm"
                aria-label={`${t("common.edit")} ${r.name}`}
                onClick={() => setEditing(r)}
              >
                {t("common.edit")}
              </Button>
              {!r.builtIn && (
                <Button
                  variant="text"
                  size="sm"
                  className="text-danger"
                  aria-label={`${t("common.delete")} ${r.name}`}
                  onClick={() => setDeleting(r)}
                >
                  {t("common.delete")}
                </Button>
              )}
            </>
          ) : null,
      },
    ],
    [t, editable],
  );
  return (
    <PageCard>
      {types.isError && <ErrorBanner error={types.error} onRetry={() => void types.refetch()} />}
      <div className="space-y-1 border-b border-line px-6 py-3 text-sm text-ink-2">
        <p>{t("userTypes.intro")}</p>
        <p>{t("userTypes.domainsExplain")}</p>
      </div>
      <DataGrid
        id="user-types"
        label={t("nav.userTypes")}
        localeText={text}
        rows={types.data?.items ?? []}
        columns={columns}
        loading={types.isFetching}
        pagination={false}
        getRowAttributes={(r) => ({ "data-testid": "user-type-row", "data-type": r.id })}
        noRowsContent={<EmptyState title={t("userTypes.empty")} />}
        toolbarActions={
          <>
            <ReloadButton onClick={() => void types.refetch()} />
            {editable && (
              <Button data-testid="add-user-type" onClick={() => setEditing("new")}>
                <Plus className="size-4" aria-hidden />
                {t("userTypes.add")}
              </Button>
            )}
          </>
        }
      />
      {editing && <UserTypeDialog type={editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => {
          if (!o) {
            setDeleting(null);
            del.reset();
          }
        }}
        title={t("common.delete")}
        confirmLabel={t("common.delete")}
        destructive
        loading={del.isPending}
        onConfirm={() => deleting && del.mutate(deleting.id)}
      >
        <p className="text-sm">{t("userTypes.deleteConfirm", { name: deleting?.name ?? "" })}</p>
        {del.isError && (
          <Alert tone="error" className="mt-3">
            {errorText(del.error)}
          </Alert>
        )}
      </ConfirmDialog>
    </PageCard>
  );
}
